/**
 * T264 — Public Find-a-Player marketplace.
 *
 * Route: /find-a-player/  (no auth required)
 *
 * Mirrors /find-a-scorer/ shape: filter sidebar + card grid → detail
 * page. Lists Player rows where is_publicly_listed=True AND
 * linked_user IS NOT NULL.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Filter, MapPin, Search } from "lucide-react";

import { findPlayers } from "../../api/coredata.api";


const FORMATS = ["T20", "ODI", "TEST", "HUNDRED", "T10", "T15"];

const ROLES = [
  { value: "",              label: "Any role" },
  { value: "BATTER",        label: "Batter" },
  { value: "BOWLER",        label: "Bowler" },
  { value: "ALL_ROUNDER",   label: "All-rounder" },
  { value: "WICKET_KEEPER", label: "Wicket keeper" },
];

const HANDS = [
  { value: "",      label: "Any" },
  { value: "RIGHT", label: "Right" },
  { value: "LEFT",  label: "Left" },
];


function PlayerCard({ player }) {
  return (
    <Link
      to={`/find-a-player/${player.id}`}
      className="block border border-bg-secondary rounded-lg p-4 bg-bg-primary hover:bg-bg-secondary/40 hover:border-button-primary/40 transition-colors"
    >
      <div className="flex gap-3">
        {player.photo_url
          ? <img src={player.photo_url} alt={player.full_name}
                 className="w-20 h-20 rounded-full object-cover border border-bg-secondary flex-shrink-0" />
          : <div className="w-20 h-20 rounded-full bg-bg-main border border-bg-secondary flex items-center justify-center text-text-secondary text-xs flex-shrink-0">No photo</div>}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 mb-1">
            <h3 className="font-semibold truncate text-text-primary">{player.full_name}</h3>
          </div>
          <div className="text-xs text-text-secondary flex items-center gap-1 mb-1">
            <MapPin className="w-3 h-3 shrink-0" />
            <span className="truncate">{[player.city, player.state].filter(Boolean).join(", ") || "—"}</span>
          </div>
          <div className="flex flex-wrap gap-1 mb-1">
            {player.role && (
              <span className="text-xs px-2 py-0.5 bg-button-primary/20 text-button-primary border border-button-primary/30 rounded">
                {player.role === "ALL_ROUNDER" ? "All-rounder" :
                 player.role === "WICKET_KEEPER" ? "WK" : player.role.toLowerCase()}
              </span>
            )}
            {(player.formats_played || []).slice(0, 3).map((f) => (
              <span key={f} className="text-xs px-2 py-0.5 bg-bg-secondary text-text-primary rounded">{f}</span>
            ))}
          </div>
          <div className="text-xs text-text-secondary">
            {player.batting_hand && `${player.batting_hand}-hand bat`}
            {player.bowling_hand && ` · ${player.bowling_hand}-arm bowl`}
            {player.daily_rate_inr ? ` · ₹${player.daily_rate_inr}/day` : ""}
          </div>
        </div>
      </div>
    </Link>
  );
}


export default function FindAPlayer() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [region, setRegion] = useState("");
  const [format, setFormat] = useState("");
  const [role, setRole] = useState("");
  const [battingHand, setBattingHand] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await findPlayers({
        q, region, format, role, batting_hand: battingHand,
      });
      setPlayers(data.players || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [region, format, role, battingHand]);

  // q is client-side filtered with debounce-light — just re-call once
  // the user pauses typing.
  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
  }, [q]);

  const inputCls =
    "mt-0.5 w-full px-2 py-1.5 text-sm rounded-lg bg-bg-main text-text-primary " +
    "border border-bg-secondary focus:border-button-primary outline-none transition-colors " +
    "placeholder:text-text-secondary/60";

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 text-text-primary">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-text-primary">Find a player</h1>
        <p className="text-text-secondary">
          Browse cricketers with verifiable match-coded stats. All listed
          players have published a public profile and consented to be
          contacted.
        </p>
      </div>

      <div className="border border-bg-secondary rounded-lg p-3 bg-bg-primary mb-4">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium text-text-secondary">
          <Filter className="w-4 h-4" /> Filter
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
          <label className="block">
            <span className="text-xs text-text-secondary">Name</span>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-text-secondary z-10" />
              <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Search name"
                     className={`${inputCls} pl-7`} />
            </div>
          </label>
          <label className="block">
            <span className="text-xs text-text-secondary">Region</span>
            <input type="text" value={region} onChange={(e) => setRegion(e.target.value)}
                   placeholder="Chennai, Mumbai…"
                   className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs text-text-secondary">Format</span>
            <select value={format} onChange={(e) => setFormat(e.target.value)}
                    className={inputCls}>
              <option value="">Any format</option>
              {FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-text-secondary">Role</span>
            <select value={role} onChange={(e) => setRole(e.target.value)}
                    className={inputCls}>
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-text-secondary">Batting hand</span>
            <select value={battingHand} onChange={(e) => setBattingHand(e.target.value)}
                    className={inputCls}>
              {HANDS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
            </select>
          </label>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-text-secondary">Loading players…</p>
      ) : players.length === 0 ? (
        <div className="text-center py-12 text-text-secondary">
          <p className="mb-2">No players match your filters.</p>
          <p className="text-xs">Try widening your search or clearing filters.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {players.map((p) => <PlayerCard key={p.id} player={p} />)}
        </div>
      )}

      <div className="mt-12 pt-6 border-t border-bg-secondary text-center text-sm text-text-secondary">
        Are you a player who'd like to be listed here?{" "}
        <Link to="/signup" className="text-button-primary hover:underline">Sign up</Link>{" "}
        or{" "}
        <Link to="/login" className="text-button-primary hover:underline">log in</Link>{" "}
        and build your public profile.
      </div>
    </div>
  );
}
